import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { UsageByUser } from '@/types/service/internalAnalytics';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { CHART_COLORS, formatCurrency, formatNumber } from '@/utils/saasDashboardHelpers';

interface LynAIUsageByUserChartProps {
  data: UsageByUser[];
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: {
      name: string;
      cost: number;
      tokens: number;
      inputTokens: number;
      outputTokens: number;
      client: string;
      email: string | null;
    };
  }>;
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className='bg-white p-3 border rounded shadow-lg'>
      <p className='font-medium text-sm mb-2'>{label}</p>
      <p className='text-sm'>Cost: {formatCurrency(data.cost)}</p>
      <p className='text-sm'>Total Tokens: {formatNumber(data.tokens)}</p>
      <p className='text-sm text-muted-foreground'>
        Input: {formatNumber(data.inputTokens)} / Output: {formatNumber(data.outputTokens)}
      </p>
      <p className='text-sm'>Client: {data.client}</p>
      {data.email && <p className='text-sm text-muted-foreground'>{data.email}</p>}
    </div>
  );
};

const LynAIUsageByUserChart = ({ data }: LynAIUsageByUserChartProps) => {
  if (data.length === 0) {
    return (
      <Card className='h-full'>
        <CardHeader>
          <CardTitle className='text-lg'>Cost by User</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[300px] flex items-center justify-center text-muted-foreground'>
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  // Transform data for chart - use display name
  const chartData = data
    .map((item) => {
      const displayName =
        item.first_name && item.last_name
          ? `${item.first_name} ${item.last_name}`
          : item.principal_name || item.email_address || 'Unknown';
      return {
        name: displayName,
        cost: parseFloat(item.total_cost_usd),
        tokens: item.total_tokens,
        inputTokens: item.total_input_tokens,
        outputTokens: item.total_output_tokens,
        client: item.client_name,
        email: item.email_address,
      };
    })
    .slice(0, 15); // Limit to top 15

  // Calculate dynamic height (50px per item, minimum 300px)
  const chartHeight = Math.max(300, chartData.length * 50);

  // Calculate dynamic Y-axis width based on longest label
  const maxLabelLength = Math.max(...chartData.map((item) => item.name.length));
  const yAxisWidth = Math.min(Math.max(maxLabelLength * 7, 120), 200);

  return (
    <Card className='h-full'>
      <CardHeader>
        <CardTitle className='text-lg'>Cost by User</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width='100%' height={chartHeight}>
          <BarChart
            data={chartData}
            layout='vertical'
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray='3 3' horizontal={true} vertical={false} />
            <XAxis type='number' tickFormatter={(value) => formatCurrency(value)} />
            <YAxis type='category' dataKey='name' width={yAxisWidth} tick={{ fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey='cost'
              name='Cost (USD)'
              fill={CHART_COLORS.pie[1]}
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default LynAIUsageByUserChart;
